import { useNavigate } from '@tanstack/react-router';
import { KeyRound, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/lib/hooks';
import { useAssistantContext } from '@/assistant/context';
import { t, tp } from '@/lib/i18n';
import { ListTemplate } from '@/templates/ListTemplate';
import { Button, Callout, Combobox, Dialog, EmptyState, Field, FilterBar, PageHeader, Switch, toast } from '@/ui';
import { DataTable } from '@/ui/DataTable';
import { useApiKeys, useRevokeApiKey, useRotateApiKey, useTransferApiKey, useUsers } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SecretReveal } from '../components/SecretReveal';
import { apiKeysRoute, type ApiKeysSearch } from '../routes';
import type { ApiKeyOut } from '../types';
import { type KeyAct, keyColumns } from './keyColumns';
import { CreateKeyDialog } from './CreateKeyDialog';

/** /admin/api-keys — machine access: create (secret shown once), rotate, hand over, revoke. */
export default function ApiKeysPage() {
  const search = apiKeysRoute.useSearch();
  const navigate = useNavigate({ from: apiKeysRoute.fullPath });
  const query = useApiKeys({ include_revoked: search.revoked === '1' });
  const rotate = useRotateApiKey();
  const revoke = useRevokeApiKey();
  const transfer = useTransferApiKey();
  const [acting, setActing] = useState<{ key: ApiKeyOut; what: KeyAct } | null>(null);
  const [heir, setHeir] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ name: string; key: string } | null>(null);
  const rows = useMemo(() => {
    const q = search.q?.trim().toLowerCase();
    return (query.data?.pages.flatMap((p) => p.items) ?? []).filter((k) => !q || `${k.name} ${k.prefix} ${k.role?.name ?? ''}`.toLowerCase().includes(q));
  }, [query.data, search.q]);
  const columns = useMemo(() => keyColumns((key, what) => setActing({ key, what })), []);
  const set = (patch: Partial<ApiKeysSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  useAssistantContext({
    title: t('API keys'),
    module: 'admin',
    filters: { q: search.q, include_revoked: search.revoked === '1' },
    visible: { type: 'api_key', ids: rows.map((k) => k.id), count: rows.length },
    suggestions: [
      t('Which API keys expire in the next month?'),
      t('Which keys have not been used for 90 days?'),
      t('What can the key for the payroll export do?'),
    ],
  });

  const k = acting?.key;
  return (
    <ListTemplate
      header={
        <PageHeader
          back={{ label: t('Admin'), to: '/admin' }}
          eyebrow={t('Admin')}
          title={t('API keys')}
          description={t(
            'Keys let other systems read or write through the API with one role on one scope. Nova stores only a fingerprint: a lost key is rotated, never shown again.',
          )}
          actions={
            <Button variant="primary" icon={Plus} onClick={() => set({ create: '1' })}>
              {t('New API key')}
            </Button>
          }
        />
      }
      filters={
        <FilterBar
          search={{ value: search.q, onChange: (q) => set({ q }), placeholder: t('Name, prefix or role') }}
          activeCount={search.revoked ? 1 : 0}
          onClear={() => set({ revoked: undefined })}
          summary={query.data ? tp(rows.length, '{count} key', '{count} keys') : undefined}
        >
          <Switch checked={search.revoked === '1'} onChange={(v) => set({ revoked: v ? '1' : undefined })} label={t('Show revoked keys')} />
        </FilterBar>
      }
    >
      <DataTable
        id="admin.api-keys"
        caption={t('API keys')}
        data={rows}
        columns={columns}
        getRowId={(x) => x.id}
        sort={search.sort}
        onSortChange={(sort) => set({ sort })}
        loading={query.isPending}
        error={query.error}
        onRetry={() => void query.refetch()}
        hasMore={query.hasNextPage}
        onLoadMore={() => !query.isFetchingNextPage && void query.fetchNextPage()}
        csvName="api-keys"
        empty={
          <EmptyState
            icon={KeyRound}
            title={search.q ? t('No key matches') : t('No API keys yet')}
            description={t('Create one for each system that talks to Nova, so each can be revoked on its own.')}
          />
        }
      />
      <CreateKeyDialog open={search.create === '1'} onOpenChange={(o) => set({ create: o ? '1' : undefined })} />
      <ConfirmDialog
        open={acting?.what === 'rotate'}
        onOpenChange={(o) => !o && setActing(null)}
        tone="primary"
        title={k ? t('Rotate {name}?', { name: k.name }) : ''}
        description={t('The key gets a new secret, shown once. The old secret stops working at once: update the system that uses it straight away.')}
        confirmLabel={t('Rotate key')}
        onConfirm={async () => {
          if (!k) return;
          const r = await rotate.mutateAsync(k.id);
          if (typeof r.data.key === 'string') setSecret({ name: k.name, key: r.data.key });
          else toast({ title: r.message, tone: 'success' });
        }}
      />
      <ConfirmDialog
        open={acting?.what === 'transfer'}
        onOpenChange={(o) => {
          if (!o) {
            setActing(null);
            setHeir(null);
          }
        }}
        tone="primary"
        title={k ? t('Hand over {name}?', { name: k.name }) : ''}
        description={t(
          'A key works only while its owner is active, and holds only what its owner holds. Hand it over before its owner leaves; the new owner must hold everything the key grants. The secret stays the same.',
        )}
        blocked={!heir}
        confirmLabel={t('Hand over key')}
        onConfirm={async () => {
          if (!k || !heir) return;
          const r = await transfer.mutateAsync({ id: k.id, to: heir });
          toast({ title: r.message, tone: 'success' });
        }}
      >
        <Field label={t('New owner')} required>
          {(f) => <HeirPicker id={f.id} value={heir} onChange={setHeir} exclude={k?.created_by?.id} />}
        </Field>
      </ConfirmDialog>
      <ConfirmDialog
        open={acting?.what === 'revoke'}
        onOpenChange={(o) => !o && setActing(null)}
        title={k ? t('Revoke {name}?', { name: k.name }) : ''}
        description={t('Every request with this key is refused from now on. This cannot be undone; create a new key if it is needed again.')}
        typeToConfirm={k?.prefix}
        confirmLabel={t('Revoke key')}
        onConfirm={async () => {
          if (!k) return;
          const r = await revoke.mutateAsync(k.id);
          toast({ title: r.message, tone: 'success' });
        }}
      />
      <Dialog
        open={!!secret}
        onOpenChange={(o) => !o && setSecret(null)}
        eyebrow={t('API keys')}
        title={secret ? t('New secret for {name}', { name: secret.name }) : ''}
        footer={
          <Button variant="primary" onClick={() => setSecret(null)}>
            {t('I have copied it')}
          </Button>
        }
      >
        {secret && <SecretReveal secret={secret.key} name={secret.name} />}
      </Dialog>
      {search.revoked === '1' && <Callout tone="neutral">{t('Revoked keys are kept for the audit trail; they never work again.')}</Callout>}
    </ListTemplate>
  );
}

/** An active person of this company, found by name or e-mail on the server. */
function HeirPicker({ id, value, onChange, exclude }: { id: string; value: string | null; onChange: (id: string | null) => void; exclude?: string }) {
  const [q, setQ] = useState('');
  const query = useDebouncedValue(q, 250);
  const users = useUsers({ q: query || undefined, active: true, external: false });
  const options = (users.data?.pages.flatMap((p) => p.items) ?? [])
    .filter((u) => u.id !== exclude)
    .map((u) => ({ value: u.id, label: u.name, description: u.email }));
  return (
    <Combobox
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      onSearch={setQ}
      loading={users.isFetching}
      placeholder={t('Choose a person')}
      searchPlaceholder={t('Name or e-mail…')}
      emptyText={t('No active person found')}
    />
  );
}
